import { Link, Stack, styled } from '@mui/joy';
import { FilePdf } from '@phosphor-icons/react';
import { ICellRendererParams } from 'ag-grid-community';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PreviewPdf } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Tooltip } from 'core/elements';
import { LinkTarget } from 'core/enums';
import { Direction, Level } from 'theme/enums';

import { getImportFileDownloadUrl, getImportFilePreviewUrl, isPreviewablePdf } from '../utils';

// Matches the datasheet icon on quote details, so the affordance reads the same
// in both places.
const PreviewIcon = styled(FilePdf)({
  cursor: 'pointer',
  flexShrink: 0,
  height: 20,
  width: 20,
});

export const FileNameCell = ({ data, value, valueFormatted }: ICellRendererParams) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const { t } = useTranslation();

  const onOpenPreview = useCallback(() => setIsPreviewOpen(true), []);
  const onClosePreview = useCallback(() => setIsPreviewOpen(false), []);

  // Group rows have no data and keep the plain (possibly empty) label.
  if (!data) {
    return <>{valueFormatted ?? value}</>;
  }

  const label = value || DEFAULT_PLACEHOLDER;
  const downloadUrl = getImportFileDownloadUrl(data.fileId);

  // An attempt the log stream never tied to an import has no file to fetch, and
  // neither does one whose file record is gone — those keep plain text rather
  // than a link that would resolve to nothing.
  if (!downloadUrl) {
    return <>{label}</>;
  }

  const downloadLink = (
    <Tooltip title={t('tooltipDownloadImportFile')}>
      {/* A real href, not an onClick: the browser handles the attachment
          response itself, and a plain link stays keyboard-reachable and
          right-clickable ("Save link as"). Not a react-router route — the
          endpoint sits outside the SPA basename, so it takes a raw href. */}
      <Link href={downloadUrl} target={LinkTarget.BLANK} level={Level.BODY_SM}>
        {label}
      </Link>
    </Tooltip>
  );

  // Spreadsheets are about a third of these uploads and the viewer cannot render
  // them, so only PDFs get the icon. The name still downloads either way.
  if (!isPreviewablePdf(data)) {
    return downloadLink;
  }

  return (
    <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1, height: '100%' }}>
      <Tooltip title={t('tooltipPreviewImportFile')}>
        <PreviewIcon onClick={onOpenPreview} />
      </Tooltip>

      {downloadLink}

      {/* Mounted only while open: this cell renders once per visible row, and the
          viewer would otherwise fetch a PDF for every one of them. */}
      {isPreviewOpen && (
        <PreviewPdf
          isOpen={isPreviewOpen}
          onClose={onClosePreview}
          fileName={label}
          fileUrl={getImportFilePreviewUrl(data.fileId)}
        />
      )}
    </Stack>
  );
};
